import React, { useCallback } from 'react';
import { activateWithCode } from '../../../api';
import { ActivationErrorType, HttpStatusCode } from '../../../constants';
import { globalMixpanelReporter, globalBigQueryReporter, globalGrafanaReporter } from '../../../events';
import { setActivationDelayCookie } from '../../../utils/setActivationDelayCookie';
import { isAxiosError } from '../../../utils/isAxiosError';
import { getEventName, getMixpanelEventName, getGrafanaEventName } from '../../../utils/eventHelpers';
import { Variant } from '../../../types';

interface UseActivateWithCodeProps {
    setError: React.Dispatch<React.SetStateAction<ActivationErrorType | null>>;
    postActivationDelay: number;
    setIsCheckingCode: (isCheckingCode: boolean) => void;
    onSuccess?: () => void;
    onError?: (error: ActivationErrorType) => void;
    variant?: Variant;
}

export const useActivateWithCode = ({
    postActivationDelay,
    setError,
    setIsCheckingCode,
    onSuccess,
    onError,
    variant = 'default',
}: UseActivateWithCodeProps) =>
    useCallback(
        async (activationCode) => {
            globalMixpanelReporter.enrichContext((context) => ({
                codeActivationAttemptsCount: (context.codeActivationAttemptsCount || 0) + 1,
            }));
            globalBigQueryReporter.enrichContext((context) => ({
                codeActivationAttemptsCount: (context.codeActivationAttemptsCount || 0) + 1,
            }));

            try {
                setError(null);
                setIsCheckingCode(true);

                globalMixpanelReporter.send(getMixpanelEventName(variant, 'submit'));
                globalBigQueryReporter.send(getEventName(variant, 'submit'));
                globalGrafanaReporter.count(getGrafanaEventName(variant, 'submit'));

                await activateWithCode(activationCode);
                setActivationDelayCookie();

                globalMixpanelReporter.send(getMixpanelEventName(variant, 'completed'));
                globalBigQueryReporter.send(getEventName(variant, 'completed'));
                globalGrafanaReporter.count(getGrafanaEventName(variant, 'completed'));

                setTimeout(() => {
                    setIsCheckingCode(false);
                    onSuccess?.();
                }, postActivationDelay);
            } catch (error) {
                let errorType: ActivationErrorType;

                const statusCode = (error as any)?.response?.status;
                if (isAxiosError(error) || statusCode) {
                    switch (statusCode) {
                        case HttpStatusCode.BAD_REQUEST:
                            errorType = ActivationErrorType.WRONG_CODE;
                            break;
                        case HttpStatusCode.FORBIDDEN:
                            errorType = ActivationErrorType.RETRY_LIMIT_REACHED;
                            break;
                        default:
                            errorType = ActivationErrorType.GENERAL;
                            break;
                    }
                } else if (error instanceof Error) {
                    if (Object.values(ActivationErrorType).includes(error.message as ActivationErrorType)) {
                        errorType = error.message as ActivationErrorType;
                    } else {
                        errorType = ActivationErrorType.GENERAL;
                    }
                } else {
                    errorType = ActivationErrorType.GENERAL;
                }

                globalMixpanelReporter.send(getMixpanelEventName(variant, 'failed'));
                globalBigQueryReporter.send(getEventName(variant, 'failed'));
                globalGrafanaReporter.count(getGrafanaEventName(variant, 'failed'));

                setError(errorType);
                setIsCheckingCode(false);
                onError?.(errorType);
            }
        },
        [onError, onSuccess, postActivationDelay, setError, setIsCheckingCode, variant]
    );
